import React, { useMemo, useState } from "react";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendingDown, TrendingUp, Clock, Shield, CheckCircle2, AlertTriangle, BarChart3 } from "lucide-react";
import type { MetricsResponse, MetricsPoint } from "../../api/client";

interface TrendChartProps {
  data: MetricsResponse;
}

export default function TrendChart({ data }: TrendChartProps) {
  const [chartType, setChartType] = useState<"burndown" | "introduced">("burndown");

  const points = data.points || [];
  const current = data.current;

  // Compute summary stats
  const stats = useMemo(() => {
    if (points.length < 2) return null;

    const first = points[0];
    const last = points[points.length - 1];
    const openDelta = last.open - first.open;
    const openPct = first.open > 0 ? Math.round((openDelta / first.open) * 100) : 0;

    const fixedDelta = last.fixed - first.fixed;

    // Daily introduced = difference in total_introduced from day to day
    const dailyIntroduced = points.slice(1).map((p, i) =>
      Math.max(0, p.total_introduced - points[i].total_introduced)
    );
    const dailyFixed = points.slice(1).map((p, i) =>
      Math.max(0, p.fixed - points[i].fixed)
    );

    const totalNewIntroduced = dailyIntroduced.reduce((a, b) => a + b, 0);
    const totalNewFixed = dailyFixed.reduce((a, b) => a + b, 0);

    // MTTR estimate: if we fixed X findings over Y days, avg ~Y/X days per fix
    const mttrDays = totalNewFixed > 0 ? (points.length / totalNewFixed).toFixed(1) : null;

    const improving = openDelta <= 0;

    return {
      openNow: last.open,
      openStart: first.open,
      openDelta,
      openPct,
      fixedTotal: last.fixed,
      fixedDelta,
      totalNewIntroduced,
      totalNewFixed,
      mttrDays,
      improving,
    };
  }, [points]);

  // Format chart data with daily deltas for introduced vs fixed chart
  const chartData = useMemo(() => {
    return points.map((p, i) => ({
      date: formatShortDate(p.date),
      fullDate: p.date,
      open: p.open,
      fixed: p.fixed,
      introduced: i > 0 ? Math.max(0, p.total_introduced - points[i - 1].total_introduced) : 0,
      dailyFixed: i > 0 ? Math.max(0, p.fixed - points[i - 1].fixed) : 0,
    }));
  }, [points]);

  if (points.length === 0) {
    return (
      <div className="card p-12 text-center">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-brand/10 text-brand ring-1 ring-brand/20 mx-auto mb-4">
          <BarChart3 className="h-7 w-7" />
        </div>
        <h3 className="text-lg font-semibold text-text">No trend data yet</h3>
        <p className="text-sm text-muted mt-2">Run baseline scans over multiple days to start tracking vulnerability trends.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Open Findings"
            value={stats.openNow}
            delta={stats.openDelta}
            deltaLabel={`from ${stats.openStart}`}
            icon={<Shield className="h-5 w-5" />}
            color={stats.improving ? "success" : "critical"}
          />
          <StatCard
            label="Total Fixed"
            value={stats.fixedTotal}
            delta={stats.fixedDelta}
            deltaLabel={`this period`}
            icon={<CheckCircle2 className="h-5 w-5" />}
            color="success"
          />
          <StatCard
            label="MTTR"
            value={stats.mttrDays ? `${stats.mttrDays}d` : "—"}
            icon={<Clock className="h-5 w-5" />}
            color="brand"
            subtitle="avg time to fix"
          />
          <StatCard
            label="Trend"
            value={`${stats.openPct > 0 ? "+" : ""}${stats.openPct}%`}
            icon={stats.improving ? <TrendingDown className="h-5 w-5" /> : <TrendingUp className="h-5 w-5" />}
            color={stats.improving ? "success" : "critical"}
            subtitle={stats.improving ? "Improving" : "Needs attention"}
          />
        </div>
      )}

      {/* Severity Breakdown */}
      {current?.by_severity && Object.keys(current.by_severity).length > 0 && (
        <div className="card p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted mb-3">Open by Severity</h3>
          <div className="flex gap-3">
            {["critical", "high", "medium", "low"].map((sev) => {
              const count = current.by_severity[sev] || 0;
              if (count === 0) return null;
              const colors: Record<string, string> = {
                critical: "bg-critical/15 text-critical border-critical/20",
                high: "bg-high/15 text-high border-high/20",
                medium: "bg-medium/15 text-medium border-medium/20",
                low: "bg-low/15 text-low border-low/20",
              };
              return (
                <div key={sev} className={`px-3 py-1.5 rounded-lg border text-sm font-bold ${colors[sev]}`}>
                  {count} <span className="font-medium text-xs capitalize">{sev}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Chart Toggle + Chart */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-semibold text-text">Vulnerability Trends</h3>
          <div className="flex gap-1 rounded-lg bg-surface2/50 p-1">
            <button
              onClick={() => setChartType("burndown")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                chartType === "burndown" ? "bg-surface3/80 text-text shadow-sm" : "text-muted hover:text-text"
              }`}
            >
              Burn-Down
            </button>
            <button
              onClick={() => setChartType("introduced")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                chartType === "introduced" ? "bg-surface3/80 text-text shadow-sm" : "text-muted hover:text-text"
              }`}
            >
              Introduced vs Fixed
            </button>
          </div>
        </div>

        <div style={{ width: "100%", height: 300 }}>
          {chartType === "burndown" ? (
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="openGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={40} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="open" stroke="#6366f1" strokeWidth={2.5} fill="url(#openGrad)" name="Open Findings" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="introGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="fixGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={40} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Area type="monotone" dataKey="introduced" stroke="#ef4444" strokeWidth={2} fill="url(#introGrad)" name="Introduced" />
                <Area type="monotone" dataKey="dailyFixed" stroke="#22c55e" strokeWidth={2} fill="url(#fixGrad)" name="Fixed" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

function StatCard({ label, value, delta, deltaLabel, icon, color, subtitle }: {
  label: string;
  value: number | string;
  delta?: number;
  deltaLabel?: string;
  icon: React.ReactNode;
  color: "success" | "critical" | "brand";
  subtitle?: string;
}) {
  const colorMap = {
    success: "text-success",
    critical: "text-critical",
    brand: "text-brand",
  };

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted">{label}</span>
        <div className={colorMap[color]}>{icon}</div>
      </div>
      <div className={`text-2xl font-extrabold ${colorMap[color]}`}>{value}</div>
      {delta !== undefined && (
        <div className="flex items-center gap-1 mt-1">
          {delta <= 0 ? (
            <TrendingDown className="h-3 w-3 text-success" />
          ) : (
            <TrendingUp className="h-3 w-3 text-critical" />
          )}
          <span className={`text-xs font-medium ${delta <= 0 ? "text-success" : "text-critical"}`}>
            {delta > 0 ? "+" : ""}{delta}
          </span>
          {deltaLabel && <span className="text-xs text-muted">{deltaLabel}</span>}
        </div>
      )}
      {subtitle && !delta && (
        <div className="text-xs text-muted mt-1">{subtitle}</div>
      )}
    </div>
  );
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="card-glass px-4 py-3 shadow-xl border border-border/40 text-sm">
      <div className="text-xs text-muted mb-1.5">{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.stroke || p.color }} />
          <span className="text-text2">{p.name}:</span>
          <span className="font-bold text-text">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
